import apiClient from './api'

export interface PresignedUrlResponse {
  uploadUrl: string
  key: string
}

export const getPresignedUploadUrl = async (
  filename: string,
  contentType: string,
  folder: 'programs' | 'products' | 'patient-documents',
): Promise<PresignedUrlResponse> => {
  const response = await apiClient.post('/uploads/presigned-url', {
    filename,
    contentType,
    folder,
  })
  return response.data.data
}

export const getPresignedViewUrl = async (key: string): Promise<string> => {
  const response = await apiClient.get(`/uploads/view-url?key=${encodeURIComponent(key)}`)
  return response.data.data.viewUrl
}

export const uploadToS3 = async (
  presignedUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', event => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100)
        onProgress(percent)
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve()
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed due to network error'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload was aborted'))
    })

    xhr.open('PUT', presignedUrl)
    xhr.setRequestHeader('Content-Type', file.type)
    xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256')
    xhr.send(file)
  })
}

export const deleteFile = async (key: string): Promise<void> => {
  await apiClient.delete('/uploads', { data: { key } })
}

export const uploadImage = async (
  file: File,
  folder: 'programs' | 'products',
  onProgress?: (percent: number) => void,
): Promise<string> => {
  const { uploadUrl, key } = await getPresignedUploadUrl(file.name, file.type, folder)
  await uploadToS3(uploadUrl, file, onProgress)
  return key
}

export const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
export const MAX_FILE_SIZE = 5 * 1024 * 1024

export const validateImageFile = (file: File): string | null => {
  if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
    return `Invalid file type. Allowed types: ${ALLOWED_IMAGE_TYPES.map(type => type.split('/')[1]).join(', ')}`
  }

  if (file.size > MAX_FILE_SIZE) {
    return `File size exceeds maximum allowed size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`
  }

  return null
}
